type Options = Partial<ModalTriggerOptions>;

export interface ModalTriggerElement extends HTMLElement {
  modalTrigger?: ModalTrigger;
}

export interface ModalTriggerOptions {
  class: string;
  onLoad: Function;
  onOpen: Function;
  shouldCache: boolean;
  targetModal: string;
}

export interface RequestOptions {
  containsExternalStylesheet?: boolean;
  url: string;
  targetElementSelector: string;
}

export interface ModalData {
  requestOptions: RequestOptions;
  class: string;
  onLoad: Function;
  onOpen: Function;
  shouldCache: boolean;
  targetModal: string;
}

export class ModalTrigger {
  static defaults = {
    class: '',
    onLoad: () => null,
    onOpen: () => null,
    shouldCache: false,
    targetModal: '[data-modal]',
  };

  element: ModalTriggerElement;
  options: ModalTriggerOptions;
  class: string;
  requestOptions: RequestOptions;

  constructor(
    element: ModalTriggerElement,
    requestOptions: RequestOptions,
    options?: Options,
  ) {
    if (!element || element.modalTrigger || !requestOptions) {
      return;
    }

    element.modalTrigger = this;
    this.element = element;

    this.options = {
      ...ModalTrigger.defaults,
      ...this.element.dataset,
      ...options,
    };

    this.requestOptions = requestOptions;

    this.bind();
  }

  bind() {
    this.element.addEventListener('click', this.handleClick);
    this.element.addEventListener('keydown', this.handleKeyDown);
  }

  handleKeyDown = (event: KeyboardEvent) => {
    if (event.keyCode === 13) {
      event.preventDefault();
      this.openModal();
    }
  };

  handleClick = (event: MouseEvent) => {
    event.preventDefault();
    this.openModal();
  };

  openModal = () => {
    const payload: ModalData = {
      class: this.options.class,
      onLoad: this.options.onLoad,
      onOpen: this.options.onOpen,
      requestOptions: this.requestOptions,
      shouldCache: this.options.shouldCache,
      targetModal: this.options.targetModal,
    };

    this.fireEvent('modal-open', this.element, payload);
  };

  fireEvent(name: string, element: HTMLElement, payload: ModalData) {
    const event = new CustomEvent(name, {
      bubbles: true,
      detail: payload,
    });

    element.dispatchEvent(event);
  }

  unbind() {
    this.element.removeEventListener('click', this.handleClick);
    this.element.removeEventListener('keydown', this.handleKeyDown);
  }

  destroy() {
    this.unbind();
    this.element.modalTrigger = undefined;
  }
}
